import { useContext } from 'react';
import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import { connect } from 'react-redux';
import get from 'lodash.get';

import { SharedItemTile } from '../../shared/SharedItemTile/SharedItemTile';
import { FavoritesProvider } from '../../components/global/FavoriteIcons/FavoritesProvider';
import { QuickViewProvider } from '../../global/QuickViewProvider/QuickViewProvider';
import { ServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext';
import { SbRespPlaceholderTile } from '../../finding/SbRespSearchResultContainer/SbRespPlaceholderTile';

import {
    VERTICAL_OFFSET_SB_RESP_IMG,
    VERTICAL_OFFSET_MOBILE_IMG,
} from '../../constants/imageConstants';
import { locations } from '../../utils/tracking/shared/favoritesTracking';

import styles from './BuySharedResultContainer.scss';
import { BuySharedItemTrackingProvider } from '../BuySharedItemTrackingProvider/BuySharedItemTrackingProvider';

const ITEMS_PER_ROW = 4;
const LAZY_LOAD_ITEM_TILE_INDEX = ITEMS_PER_ROW * 3;

function BuySharedResultContainerComponent({
    viewer,
    buyPage,
    relay,
    relayVariables,
    refetchInFlight,
}) {
    const { isFavoritesEnabled } = viewer;
    const { edges: items } = buyPage;

    const { isMobile } = useContext(ServerVarsContext);

    return (
        <div className={styles.container}>
            <FavoritesProvider
                disable={!relayVariables.isClient}
                environment={relay.environment}
                itemIds={items.map(({ node }) => get(node, 'item.serviceId'))}
                // location should be kept the same GROWTH-4297
                location={locations.SEARCH_BROWSE}
                userId={relayVariables.userId}
                fetchFolder={!isMobile}
                fetchHeart={isFavoritesEnabled}
            >
                {({ props: favorites }) => (
                    <QuickViewProvider>
                        {({ quickView }) => (
                            <BuySharedItemTrackingProvider buyPage={buyPage} viewer={viewer}>
                                {({ fireItemImpressionTracking, fireItemClickTracking }) =>
                                    items.map(({ node: { item } }, index) => {
                                        if (refetchInFlight && !isMobile) {
                                            return <SbRespPlaceholderTile key={item.serviceId} />;
                                        }
                                        return (
                                            <SharedItemTile
                                                showCreatorName
                                                showSellerName={false}
                                                isMobile={isMobile}
                                                key={item.serviceId}
                                                item={item}
                                                favorites={isFavoritesEnabled && favorites}
                                                index={index}
                                                quickViewProps={quickView}
                                                lazyLoadStartIndex={
                                                    isMobile ? undefined : LAZY_LOAD_ITEM_TILE_INDEX
                                                }
                                                isClient={relayVariables.isClient}
                                                imageLoadVerticalOffset={
                                                    isMobile
                                                        ? VERTICAL_OFFSET_MOBILE_IMG
                                                        : VERTICAL_OFFSET_SB_RESP_IMG
                                                }
                                                onContentVisible={() =>
                                                    fireItemImpressionTracking(index)
                                                }
                                                onClick={() => fireItemClickTracking(index)}
                                                customTileWidthClass={styles.tileWidth}
                                                viewer={viewer}
                                                itemSearch={buyPage}
                                            />
                                        );
                                    })
                                }
                            </BuySharedItemTrackingProvider>
                        )}
                    </QuickViewProvider>
                )}
            </FavoritesProvider>
        </div>
    );
}

BuySharedResultContainerComponent.propTypes = {
    viewer: PropTypes.object.isRequired,
    buyPage: PropTypes.object.isRequired,
    relay: PropTypes.object.isRequired,

    // redux
    relayVariables: PropTypes.object.isRequired,
    refetchInFlight: PropTypes.bool,
};

function mapStateToProps(state) {
    return {
        relayVariables: get(state, 'relayVariables.variables'),
        refetchInFlight: get(state, 'filters.refetchInFlight'),
    };
}

export const BuySharedResultContainer = createFragmentContainer(
    connect(mapStateToProps)(BuySharedResultContainerComponent),
    {
        viewer: graphql`
            fragment BuySharedResultContainer_viewer on Viewer {
                isFavoritesEnabled: isEnabledByLocale(featureName: "NON_MVP_SEARCH_BROWSE")
                ...SharedItemTile_viewer
                ...BuySharedItemTrackingProvider_viewer
            }
        `,
        buyPage: graphql`
            fragment BuySharedResultContainer_buyPage on ItemSearchQueryConnection {
                ...SharedItemTile_itemSearch
                edges {
                    node {
                        item {
                            serviceId
                            ...SharedItemTile_item
                        }
                    }
                }
                ...BuySharedItemTrackingProvider_buyPage
            }
        `,
    }
);
